<template>
    <div
        ref="root"
        :style="pageStyle"
        class="d-flex flex-column justify-content-center position-relative"
    >
        <canvas
            v-show="rendered"
            ref="canvas"
            class="mx-auto"
            v-bind="canvasAttrs"
        />

        <div
            ref="dataLayer"
            :class="[
                'pdfjs__text-layer pdfjs__annotation-layer mx-auto',
                { 'pdfjs__text-no-select': !selectable },
            ]"
            :style="canvasAttrs.style"
        />

        <div
            v-if="!rendered"
            class="d-flex align-items-center justify-content-center bg-light mx-auto"
            :style="canvasAttrs.style"
        >
            <loading-spinner :width="70" />
        </div>
    </div>
</template>

<script>
import { toRaw } from "vue"
import debounce from "lodash/debounce"
import isNumber from "lodash/isNumber"
import LoadingSpinner from "~/components/LoadingSpinner"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

import Semaphore from "~/lib/Semaphore"

class LinkService {
    constructor({ onGoToPage, onGoToDestination }) {
        this.onGoToPage = onGoToPage
        this.onGoToDestination = onGoToDestination

        // All links will open a new tab
        this.externalLinkTarget = 2
    }

    addLinkAttributes(link, url) {
        link.href = url
        link.target = "_blank"
    }

    getDestinationHash() {
        return ""
    }

    goToDestination(destination) {
        this.onGoToDestination(destination)
    }

    navigateTo(nav) {
        const [pageRef] = nav
        if (isNumber(pageRef)) {
            this.onGoToPage(pageRef)
        } else {
            this.onGoToDestination(nav)
        }
    }
}

const overscale = 2

// Render pages one by one
const semaphore = new Semaphore(1)

export default {
    props: {
        pdfjs: Object,
        pdf: Object,
        number: Number,
        visible: Boolean,
        bodyWidth: Number,
        bodyHeight: Number,
        fit: String,
        selectable: {
            type: Boolean,
            default: true,
        },
    },

    emits: ["goto-page", "goto-destination"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            rendered: false,
            width: 0,
            height: 0,
            devicePixelRatio: null,
        }
    },

    computed: {
        pageStyle() {
            if (this.height) {
                return {
                    minHeight: `${Math.ceil(this.height / overscale)}px`,
                }
            } else {
                return {
                    minHeight: `100%`,
                }
            }
        },

        canvasAttrs() {
            const w = Math.ceil(this.width)
            const h = Math.ceil(this.height)
            return {
                width: w,
                height: h,
                style: {
                    width: `${w / overscale}px`,
                    height: `${h / overscale}px`,
                },
            }
        },
    },

    watch: {
        visible() {
            this.update()
        },

        fit() {
            this.update()
        },
    },

    async created() {
        this.update = debounce(this.update, 100)
        this.update()
        this.devicePixelRatio = window.devicePixelRatio
    },

    mounted() {
        this.listen("resize", () => {
            if (this.devicePixelRatio == window.devicePixelRatio) {
                this.update()
            }
            this.devicePixelRatio = window.devicePixelRatio
        })
        this.$refs.dataLayer?.addEventListener("copy", this.handleCopy)
    },

    beforeUnmount() {
        this.$refs.dataLayer?.removeEventListener("copy", this.handleCopy)
    },

    methods: {
        handleCopy(event) {
            const selection = document.getSelection()
            if (!selection || !event.clipboardData) return
            const text = selection.toString().replace(/\n{2,}/g, "\n")
            event.clipboardData.setData("text/plain", text)
            event.preventDefault()
        },

        async update() {
            if (this.visible) {
                await this.render()
            } else {
                await this.unrender()
            }
        },

        async render() {
            await semaphore.acquire()

            try {
                if (!this.pdf || !this.$refs.root) {
                    console.warn("No PDF or page ref before rendering")
                    return
                }

                const page = await toRaw(this.pdf).getPage(this.number)

                // Check again after async operation in case PDF was destroyed
                if (!this.pdf || !this.$refs.root) {
                    console.warn("No PDF or page ref before rendering 2")
                    return
                }

                const originalViewport = page.getViewport({ scale: 1 })

                const documentAspectRatio =
                    originalViewport.width / originalViewport.height
                const bodyAspectRatio = this.bodyWidth / this.bodyHeight

                let scale
                if (
                    documentAspectRatio < bodyAspectRatio &&
                    this.fit == "height"
                ) {
                    // fit to height
                    scale = this.bodyHeight / originalViewport.height
                } else {
                    // fit to width
                    const pageElement = this.$refs.root
                    if (!pageElement) {
                        console.warn("No page element ref")
                        return
                    }
                    scale = pageElement.clientWidth / originalViewport.width
                }

                const scaledViewport = page.getViewport({ scale })
                this.$refs.root?.style.setProperty(
                    "--scale-factor",
                    scaledViewport.scale
                )
                const overscaledViewport = page.getViewport({
                    scale: scale * overscale,
                })

                this.width = overscaledViewport.width
                this.height = overscaledViewport.height

                const canvas = this.$refs.canvas
                if (!canvas) {
                    console.warn("No canvas ref")
                    return
                }
                await page.render({
                    canvasContext: canvas.getContext("2d"),
                    viewport: overscaledViewport,
                }).promise

                const annotations = await page.getAnnotations()

                const container = this.$refs.dataLayer
                if (!container) {
                    console.warn("No dataLayer ref")
                    return
                }

                // Destroy previous annotations
                container.innerHTML = ""

                const linkService = new LinkService({
                    onGoToPage: pageRef => {
                        this.$emit("goto-page", pageRef)
                    },
                    onGoToDestination: destination => {
                        this.$emit("goto-destination", destination)
                    },
                })

                const textLayer = new this.pdfjs.TextLayer({
                    textContentSource: page.streamTextContent(),
                    container,
                    viewport: scaledViewport,
                })

                const annotationLayer = new this.pdfjs.AnnotationLayer({
                    div: container,
                    page,
                    viewport: scaledViewport.clone({ dontFlip: true }),
                    linkService,
                })

                await Promise.all([
                    textLayer.render(),
                    annotationLayer.render({
                        viewport: scaledViewport.clone({ dontFlip: true }),
                        div: container,
                        annotations,
                        page,
                        linkService,
                        renderForms: false,
                    }),
                ])

                this.rendered = true
            } catch (error) {
                console.error("Error rendering PDF page:", error)
                return
            } finally {
                semaphore.release()
            }
        },

        unrender() {
            this.rendered = false
        },
    },

    components: {
        LoadingSpinner,
    },
}
</script>

<style>
/* These can't be scoped! */
/*
 * pdfjs normally computes --total-scale-factor under .pdfViewer .page and
 * text-span styles under .textLayer. Since we use custom class names we must
 * replicate those rules here.
 */
.pdfjs__annotation-layer,
.pdfjs__text-layer {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    opacity: 0.2;
    line-height: 1;
    --user-unit: 1;
    --total-scale-factor: calc(var(--scale-factor) * var(--user-unit));
    --scale-round-x: 1px;
    --scale-round-y: 1px;
    /* --min-font-size is set as an inline style by TextLayer; derive the rest here */
    --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
    --min-font-size-inv: calc(1 / var(--min-font-size));
}

.pdfjs__annotation-layer > section {
    color: transparent;
    position: absolute;
    white-space: pre;
}

.pdfjs__text-layer > :not(.markedContent),
.pdfjs__text-layer .markedContent span:not(.markedContent) {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0% 0%;
    --font-height: 0;
    font-size: calc(var(--text-scale-factor) * var(--font-height));
    --scale-x: 1;
    --rotate: 0deg;
    transform: rotate(var(--rotate)) scaleX(var(--scale-x))
        scale(var(--min-font-size-inv));
}

.pdfjs__text-layer .markedContent {
    display: contents;
}

.pdfjs__annotation-layer > .linkAnnotation > a {
    position: absolute;
    font-size: 1em;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.pdfjs__text-no-select > :not(section):not(.markedContent),
.pdfjs__text-no-select .markedContent span:not(.markedContent) {
    user-select: none;
    pointer-events: none;
    cursor: default;
}
</style>
